import { AddressController } from '@adornis/address/client/address-controller.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { getBestEncodingForUserAgent } from '@adornis/import-export/client/encoding-helpers.js';
import { ExportJob } from '@adornis/import-export/db/export-job.js';
import { openPopupWithLoadingScreen } from '@adornis/print/client/safe-popup.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { LearningDataAPI } from '@chancenwerk/campus-learning-content/api/LearningDataAPI.js';
import { LearningDataExportFilter } from '@chancenwerk/campus-learning-content/api/exportLearningData.js';
import { Bundesland } from '@chancenwerk/db/enums/enums.js';
import { KpfFilter } from '@chancenwerk/db/filter/KpfFilter.js';
import { ChancenPermission } from '@chancenwerk/db/permissions.js';
import { CrPage } from '@chancenwerk/design/components/cr-page.js';
import { KPFAPI } from '@chancenwerk/master-data-management/kpf/api/KPFAPI.js';
import { KPF } from '@chancenwerk/master-data-management/kpf/entities/KPF.js';
import { ChancenwerkUser } from '@chancenwerk/master-data-management/users/entities/ChancenwerkUser.js';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { firstValueFrom } from 'rxjs';

/**
 * @element campus-export-learning-card
 */
@customElement('campus-export-learning-card')
export class CampusExportLearningCard extends ChemistryLitElement {
  @property({ attribute: true, type: Boolean }) public active = false;

  override render() {
    return html`
      <cr-card
        padding="sm"
        ${css({ background: this.active ? this.colors.primary.shade(200) : this.colors.tone.error.shade(200) })}
      >
        <cr-flex
          space="md"
          padding="sm"
          ${css({ borderRadius: this.sizes.borderRadiusSecondary, background: this.colors.white })}
        >
          <slot></slot>
        </cr-flex>
      </cr-card>
    `;
  }
}

/**
 * @element campus-page-export-learning-data
 */
@customElement('campus-page-export-learning-data')
export class CampusPageExportLearningData extends CrPage {
  constructor() {
    super();
    this.section = false;
    this.label = 'Export der Lerndaten';
    this.description =
      'Hier kannst du einen Rohdaten-Export der Lerndaten vornehmen. Zusätzlich kannst du hier Filter einstellen um die Daten weiter einzuschränken. Die aktiven Filter sind grün hinterlegt, während inaktive rot sind. Dies liegt daran, dass sich verschiedene Einstellungen ausschließen. Zum Beispiel Städte und Bundesländer.';
  }

  @state() private readonly _exportFilter = new RXController(this, new LearningDataExportFilter({}));

  public addressController = new AddressController(this, { language: 'de' });

  private readonly _form = new FormController(this, this._exportFilter.observable, {
    success: async filter => {
      this._exportLearningData(filter);
    },
  });

  private readonly _formMapping = new FormController(this, this._exportFilter.observable, {
    success: async filter => {
      this._exportLearningDataMapping(filter);
    },
  });

  protected override header(): Renderable {
    return html`<cr-flex horizontal crossaxis-center space="md">
      <cr-button
        icon="file_download"
        @click=${() => {
          void this._form.validate();
        }}
      >
        Exportieren
      </cr-button>
      ${permissionGuard(ChancenPermission.campus.EXPORT_LEARNING_DATA_MAPPING, () => {
        return html`<cr-button
          icon="file_download"
          @click=${() => {
            void this._formMapping.validate();
          }}
        >
          Zuweisung Exportieren
        </cr-button>`;
      })}
    </cr-flex> `;
  }

  protected override content(): Renderable {
    return html`
      <cr-flex space="sm" padding="xs">
        <campus-export-learning-card flex .active=${this._exportFilter.value.grades?.length}>
          <cr-h4> Jahrgangsstufe </cr-h4>
          <cr-dropdown-multiselection
            clearable
            placeholder="Die Jahrgangstufen die exportiert werden sollen."
            placeholder-mode="hide"
            ${this._form.field('grades')}
            .selectables=${ChancenwerkUser.availableGrades}
            @value-picked=${(e: ValueEvent<Maybe<number[]>>) => {
              this.requestUpdate();
            }}
          ></cr-dropdown-multiselection>
        </campus-export-learning-card>
        <cr-flex space="sm" wrap horizontal>
          <campus-export-learning-card
            flex
            .active=${this._exportFilter.value.bundeslaender?.length && !this._exportFilter.value.kpfIDs?.length}
          >
            <cr-h4> Bundesländer </cr-h4>
            <cr-dropdown-multiselection
              clearable
              placeholder="Welche Schüler aus welchen Bundesländer beachtet werden?"
              placeholder-mode="hide"
              ${this._form.field('bundeslaender')}
              .selectables=${Object.values(Bundesland)}
              @value-picked=${(e: ValueEvent<Maybe<string[]>>) => {
                this.requestUpdate();
              }}
            ></cr-dropdown-multiselection>
          </campus-export-learning-card>

          <campus-export-learning-card
            flex
            .active=${this._exportFilter.value.cities?.length &&
            !this._exportFilter.value.bundeslaender?.length &&
            !this._exportFilter.value.kpfIDs?.length}
          >
            <cr-h4> Stadt </cr-h4>
            <cr-dropdown-search-selection
              flex
              icon="location_city"
              placeholder="Städte hinzufügen"
              ${this.addressController.city()}
              @value-picked=${async (e: ValueEvent<string>) => {
                const value = e.detail.value;
                if (!value.includes(',')) {
                  this._exportFilter.value.cities?.push(value);
                }

                // @ts-expect-error value is an attribute
                if (e.target) e.target.value = null;
                this.requestUpdate();
              }}
            ></cr-dropdown-search-selection>
            ${repeat(this._exportFilter.value.cities ?? [], (city, index) => {
              return html`
                <cr-card crossaxis-center flex space="sm" padding="sm" horizontal space-between>
                  <cr-text>${city}</cr-text>
                  <cr-button
                    mode="icon"
                    icon="delete"
                    tone="error"
                    @click=${() => {
                      this._exportFilter.value.cities?.splice(index, 1);
                      this.requestUpdate();
                    }}
                  ></cr-button>
                </cr-card>
              `;
            })}
          </campus-export-learning-card>
        </cr-flex>

        <campus-export-learning-card flex .active=${this._exportFilter.value.kpfIDs?.length}>
          <cr-h4> KPFs </cr-h4>
          <cr-dropdown-multiselection
            clearable
            placeholder="Aus welchen KPF / Schulen sollen die Daten kommen?"
            placeholder-mode="hide"
            .renderString=${(kpf: KPF) => `${kpf.kuerzel} | ${kpf.school?.name}`}
            .search=${(search: Maybe<string>) =>
              firstValueFrom(
                KPFAPI.subscribeByFilter(
                  0,
                  20,
                  new KpfFilter({ search }),
                )({
                  kuerzel: 1,
                  schoolID: 1,
                  school: {
                    name: 1,
                    kuerzel: 1,
                  },
                  produkt: {
                    kuerzel: 1,
                  },
                }),
              )}
            @value-picked=${(e: ValueEvent<Maybe<KPF[]>>) => {
              this._exportFilter.value.kpfIDs = e.detail.value ? e.detail.value.map(kpf => kpf._id) : [];
              this.requestUpdate();
            }}
          ></cr-dropdown-multiselection>
        </campus-export-learning-card>

        <campus-export-learning-card
          flex
          .active=${!!this._exportFilter.value.startDate || !!this._exportFilter.value.endDate}
        >
          <cr-h4> Zeitfenster </cr-h4>
          <cr-text>Der Filter vergleicht größer/gleich für das Startdatum und kleiner/gleich für das Enddatum.</cr-text>
          <cr-flex horizontal space="sm" crossaxis-center>
            <cr-date-picker-dropdown
              clearable
              click-to-save
              flex
              placeholder="Startdatum"
              .value=${this._exportFilter.value.startDate}
              @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
                this._exportFilter.value.startDate = e.detail.value?.startOf('day') ?? null;
                this.requestUpdate();
              }}
            ></cr-date-picker-dropdown>
            <cr-text>-</cr-text>
            <cr-date-picker-dropdown
              clearable
              click-to-save
              flex
              placeholder="Enddatum"
              .value=${this._exportFilter.value.endDate}
              @value-picked=${(e: ValueEvent<Maybe<DateTime>>) => {
                this._exportFilter.value.endDate = e.detail.value?.endOf('day') ?? null;
                this.requestUpdate();
              }}
            ></cr-date-picker-dropdown>
          </cr-flex>
        </campus-export-learning-card>
      </cr-flex>
    `;
  }

  private async _exportLearningData(filter: LearningDataExportFilter) {
    await openPopupWithLoadingScreen(
      LearningDataAPI.exportWithFilter(filter)().then(content =>
        ExportJob.exportStringAsFile(
          content,
          DateTime.now().toFormat('yyyy.LL.dd') + '_lerndaten.csv',
          getBestEncodingForUserAgent(),
        )(),
      ),
      {
        loadingText: DateTime.now().toFormat('yyyy.LL.dd') + '_lerndaten.csv',
        closeAfterResolve: true,
        spinnerColor: this.colors.accent.string,
      },
    );
  }

  private async _exportLearningDataMapping(filter: LearningDataExportFilter) {
    await openPopupWithLoadingScreen(
      LearningDataAPI.exportMappingWithFilter(filter)().then(content =>
        ExportJob.exportStringAsFile(
          content,
          DateTime.now().toFormat('yyyy.LL.dd') + '_lerndaten_zuordnung.csv',
          getBestEncodingForUserAgent(),
        )(),
      ),
      {
        loadingText: DateTime.now().toFormat('yyyy.LL.dd') + '_lerndaten_zuordnung.csv',
        closeAfterResolve: true,
        spinnerColor: this.colors.accent.string,
      },
    );
  }
}
